import type { Locale as MelwaysLocale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Text } from 'braid-design-system';
import {
  differenceInCalendarMonths,
  formatDate,
  formatDistanceStrict,
  type Locale,
} from 'date-fns';
import { enAU as en, id, th, zhHK } from 'date-fns/locale';
import type { ComponentProps, FC } from 'react';

import translations from './.vocab';
import type { MonthYear } from './types';

type Maybe<T> = T | null;

interface Month {
  month?: number;
  year?: number;
}

const languageToLocaleMap: Record<MelwaysLocale, Locale> = {
  'en-AU': en,
  'en-HK': en,
  'en-ID': en,
  'en-MY': en,
  'en-NZ': en,
  'en-PH': en,
  'en-SG': en,
  'en-TH': en,
  'id-ID': id,
  'th-TH': th,
  'zh-HK': zhHK,
};

const formatMonthYear = (
  { year, month }: MonthYear,
  locale: keyof typeof languageToLocaleMap,
): string => {
  const date = new Date(year, month - 1, 1);

  return formatDate(date, 'MMM yyyy', {
    locale: languageToLocaleMap[locale],
  });
};

const getDateFromMonthInput = (
  { month, year }: Month,
  rounding: 'roundUp' | 'roundDown',
): Date | null => {
  if (!(month && year)) {
    return null;
  }
  const monthToCheck = rounding === 'roundUp' ? month : month - 1;
  return new Date(year, monthToCheck, 1);
};

const useTimeSpan = ({
  fromInput,
  toInput,
  melwaysLocale,
}: {
  fromInput: Month;
  toInput?: Maybe<Month>;
  melwaysLocale: MelwaysLocale;
}): string => {
  const { t } = useTranslations(translations);
  const from = getDateFromMonthInput(fromInput, 'roundDown');
  const to = toInput ? getDateFromMonthInput(toInput, 'roundUp') : new Date();
  const dateFnsLocale = languageToLocaleMap[melwaysLocale];
  const options = { locale: dateFnsLocale };

  if (from === null || to === null) {
    return '';
  }

  // Cater for scenario with no toInput provided
  // Example when user select `Still in Role` at career history
  const diffInMonths = differenceInCalendarMonths(to, from);
  if (diffInMonths <= 1) {
    return t('1 month or less');
  }

  const years = Math.floor(diffInMonths / 12);
  if (years < 1) {
    return formatDistanceStrict(from, to, options);
  }

  const yearsString = formatDistanceStrict(from, to, {
    ...options,
    roundingMethod: 'floor',
    unit: 'year',
  });

  const months = diffInMonths % 12;
  const currentDate = new Date();
  const dateInFutureMonths = new Date().setMonth(
    new Date().getMonth() + months,
  );
  const monthsString =
    months > 0
      ? formatDistanceStrict(currentDate, dateInFutureMonths, {
          ...options,
          roundingMethod: 'floor',
          unit: 'month',
        })
      : '';

  return `${yearsString}${monthsString ? ' ' : ''}${monthsString}`;
};

interface FormattedDateSpanProps {
  from: MonthYear;
  to?: MonthYear | null;
  textProps?: ComponentProps<typeof Text>;
  locale: MelwaysLocale;
}

export const FormattedDateSpan: FC<FormattedDateSpanProps> = ({
  from,
  to,
  locale,
  textProps,
}) => {
  const { t } = useTranslations(translations);

  // This is taken from `ca-myprofile-utils`https://github.com/SEEK-Jobs/ca-myprofile-utils/blob/6544427032221e14c6741aed21de6165eda31615/src/formatters/index.ts#L119-L133
  const formattedFromDate = formatMonthYear(from, locale);
  const formattedToDate = to ? formatMonthYear(to, locale) : t('Present');

  const roleLength = useTimeSpan({
    fromInput: from,
    toInput: to,
    melwaysLocale: locale,
  });

  const formattedDate = `${formattedFromDate} - ${formattedToDate} (${roleLength})`;

  return (
    <Text component="time" {...textProps}>
      {formattedDate}
    </Text>
  );
};
